import React, { useEffect, useRef, useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import styles from './diagnoses.module.css';
import { DataProviderInstance } from '../../../../api/DataProvider';
import { MIN_QUERY_LENGTH, classificationOf } from '../../modules/diagnoses/diagnoses';

const DEBOUNCE_MS = 300;

/**
 * Campo de busca na CID-10 ou na CID-11: digita código ("F32.1", "8A80") ou
 * parte do nome ("enxaqueca aura") e escolhe na lista. A busca roda no
 * servidor, limitada, com um pequeno atraso para não disparar a cada tecla.
 *
 * `classification`: 'cid10' | 'cid11'; trocar refaz a busca.
 * `onSelect(code)`: código escolhido ({ code, title, ... }).
 * `excludeCodes`: códigos já escolhidos neste atendimento (saem da lista).
 */
const IcdSearch = ({ classification, onSelect, excludeCodes = [], disabled = false }) => {
  const { SearchIcd } = DataProviderInstance();
  const info = classificationOf(classification);
  const [input, setInput] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const requestRef = useRef(0);

  useEffect(() => {
    const query = input.trim();
    const request = ++requestRef.current;
    if (query.length < MIN_QUERY_LENGTH) {
      setOptions([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const timer = setTimeout(() => {
      SearchIcd(info.key, query, (response) => {
        // Só a resposta da última busca vale (as anteriores podem chegar depois)
        if (request !== requestRef.current) return;
        setLoading(false);
        setOptions(response && response.status === 'SUCCESS' && Array.isArray(response.data) ? response.data : []);
      });
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input, info.key]);

  const visible = options.filter((option) => !excludeCodes.includes(option.code));
  const noOptionsText = input.trim().length < MIN_QUERY_LENGTH
    ? 'Digite o código ou parte do nome'
    : 'Nenhum código encontrado';

  return (
    <Autocomplete
      size="small"
      options={visible}
      value={null}
      inputValue={input}
      disabled={disabled}
      loading={loading}
      filterOptions={(list) => list}
      getOptionLabel={(option) => `${option.code} ${option.title}`}
      isOptionEqualToValue={(option, value) => option.code === value.code}
      noOptionsText={noOptionsText}
      loadingText="Buscando..."
      onInputChange={(event, value, reason) => {
        if (reason !== 'reset') setInput(value);
      }}
      onChange={(event, option) => {
        if (!option) return;
        setInput('');
        setOptions([]);
        onSelect(option);
      }}
      renderOption={(props, option) => (
        <li {...props} key={option.code}>
          <div className={styles.option}>
            <span className={styles.optionCode}>{option.code}</span>
            <span className={styles.optionTitle}>{option.title}</span>
          </div>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={`Buscar na ${info.label}`}
          placeholder={info.placeholder}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress color="inherit" size={16} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default IcdSearch;
